import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Cookie, Shield, Eye, Database, Settings, Users, CheckCircle, AlertCircle, Clock, FileText, Mail, Phone } from 'lucide-react';

export default function CookiePolicy() {
 const cookieTypes = [
 {
 type: "Essential Cookies",
 icon: Shield,
 description: "Required for basic site functionality",
 examples: ["Session management", "Authentication", "Security"],
 duration: "Session-based or up to 24 hours",
 mandatory: true
 },
 {
 type: "Functional Cookies",
 icon: Settings,
 description: "Remember your preferences and settings",
 examples: ["Language selection", "Theme preferences", "Region settings"],
 duration: "30 days to 1 year",
 mandatory: false
 },
 {
 type: "Analytics Cookies",
 icon: Database,
 description: "Help us understand how visitors use our site",
 examples: ["Page visits", "Time spent", "Navigation paths"],
 duration: "24 hours to 2 years",
 mandatory: false
 },
 {
 type: "Marketing Cookies",
 icon: Users,
 description: "Track visitors across websites for targeted advertising",
 examples: ["Ad personalization", "Campaign tracking", "Social media integration"],
 duration: "90 days to 2 years",
 mandatory: false
 }
 ];

 const cookieFeatures = [
 {
 icon: CheckCircle,
 title: "No Third-Party Sharing",
 description: "We don't sell or share cookie data with third parties"
 },
 {
 icon: Eye,
 title: "Transparent Tracking",
 description: "Full visibility into what data we collect"
 },
 {
 icon: Shield,
 title: "GDPR Compliant",
 description: "Fully compliant with data protection regulations"
 },
 {
 icon: Clock,
 title: "Clear Expiration",
 description: "All cookies have defined expiration periods"
 }
 ];

 const cookieManagement = [
 {
 browser: "Google Chrome",
 link: "https://support.google.com/chrome/answer/95647",
 steps: "Settings → Privacy and security → Cookies and other site data"
 },
 {
 browser: "Mozilla Firefox",
 link: "https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop",
 steps: "Options → Privacy & Security → Cookies and Site Data"
 },
 {
 browser: "Safari",
 link: "https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac",
 steps: "Preferences → Privacy → Cookies and website data"
 },
 {
 browser: "Microsoft Edge",
 link: "https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09",
 steps: "Settings → Cookies and site permissions → Cookies and site data"
 }
 ];

 return (
 <div className="min-h-screen ">
 {/* Hero Section */}
 <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-4xl mx-auto">
 <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-primary to-primary/80 shadow-lg mb-4 sm:mb-6">
 <Cookie className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
 </div>
 <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6">
 Cookie Policy
 </h1>
 <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8">
 Understanding how we use cookies to enhance your experience on Shebeka
 </p>
 <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 text-green-800 dark:text-green-300 rounded-full text-xs sm:text-sm font-medium">
 GDPR Compliant
 </div>
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 text-blue-800 dark:text-blue-300 rounded-full text-xs sm:text-sm font-medium">
 No Data Selling
 </div>
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 text-purple-800 dark:text-purple-300 rounded-full text-xs sm:text-sm font-medium">
 Cookie Consent Required
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* Main Content */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Cookie Features Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
 {cookieFeatures.map((feature) => {
 const Icon = feature.icon;
 return (
 <Card
 key={feature.title}
 className="text-center hover:shadow-lg hover:scale-105 transition-all duration-300 border-border"
 >
 <CardContent className="pt-6 pb-5">
 <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
 <Icon className="w-6 h-6 text-primary" />
 </div>
 <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">{feature.title}</h3>
 <p className="text-xs sm:text-sm text-muted-foreground">{feature.description}</p>
 </CardContent>
 </Card>
 );
 })}
 </div>

 {/* Introduction */}
 <Card className="mb-8 sm:mb-12 border-primary/20">
 <CardContent className="p-4 sm:p-6 md:p-8">
 <div className="flex flex-col sm:flex-row items-start gap-4">
 <div className="flex-shrink-0">
 <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
 </div>
 <div>
 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed">
 This Cookie Policy explains how <span className="font-bold text-primary">Shebeka</span> uses cookies and similar technologies to recognize you when you visit our platform. It explains what these technologies are, why we use them, and your rights to control our use of them.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* What Are Cookies */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Cookie className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 What Are Cookies?
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
 <Card className="border-border">
 <CardHeader>
 <CardTitle className="text-lg sm:text-xl">Definition</CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-sm sm:text-base text-muted-foreground mb-4">
 Cookies are small text files that are placed on your device (computer, tablet, or mobile) when you visit websites. They are widely used to make websites work more efficiently and provide information to the owners.
 </p>
 <div className="space-y-2">
 <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-primary"></div>
 <span>Typically store user preferences</span>
 </div>
 <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-primary/70"></div>
 <span>Help websites remember your settings</span>
 </div>
 </div>
 </CardContent>
 </Card>
 
 <Card className="bg-primary/5 border-primary/20">
 <CardHeader>
 <CardTitle className="text-lg sm:text-xl">Why We Use Them</CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2 sm:space-y-3">
 {[
 "To enable basic website functionality",
 "To remember your preferences and settings",
 "To analyze how visitors use our platform",
 "To improve user experience and platform performance"
 ].map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
 <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Types of Cookies */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Database className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Types of Cookies We Use
 </h2>
 <div className="space-y-4 sm:space-y-6">
 {cookieTypes.map((cookie) => {
 const Icon = cookie.icon;
 return (
 <Card
 key={cookie.type}
 className={`hover:shadow-lg transition-all duration-300 ${
 cookie.mandatory 
 ? 'border-green-200 dark:border-green-800/50' 
 : 'border-border'
 }`}
 >
 <CardContent className="p-4 sm:p-6">
 <div className="flex flex-col sm:flex-row sm:items-start gap-4">
 <div className="flex-shrink-0">
 <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center ${
 cookie.mandatory 
 ? 'bg-green-100 ' 
 : 'bg-primary/10'
 }`}>
 <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${
 cookie.mandatory 
 ? 'text-green-600 dark:text-green-400' 
 : 'text-primary'
 }`} />
 </div>
 </div>
 <div className="flex-grow">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
 <h3 className="text-lg sm:text-xl font-bold text-foreground">{cookie.type}</h3>
 {cookie.mandatory && (
 <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-800 dark:text-green-300 rounded-full text-xs font-bold w-fit">
 ESSENTIAL
 </span>
 )}
 </div>
 <p className="text-sm sm:text-base text-muted-foreground mb-4">{cookie.description}</p>
 
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
 <div className=" rounded-lg p-3 sm:p-4">
 <h4 className="font-bold text-foreground text-xs sm:text-sm mb-2">Examples</h4>
 <ul className="space-y-1">
 {cookie.examples.map((example, idx) => (
 <li key={idx} className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2">
 <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
 {example}
 </li>
 ))}
 </ul>
 </div>
 <div className=" rounded-lg p-3 sm:p-4">
 <h4 className="font-bold text-foreground text-xs sm:text-sm mb-2">Duration</h4>
 <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2">
 <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
 {cookie.duration}
 </p>
 </div>
 <div className=" rounded-lg p-3 sm:p-4">
 <h4 className="font-bold text-foreground text-xs sm:text-sm mb-2">Status</h4>
 <p className={`text-xs sm:text-sm font-medium ${
 cookie.mandatory 
 ? 'text-green-600 dark:text-green-400' 
 : 'text-blue-600 dark:text-blue-400'
 }`}>
 {cookie.mandatory ? 'Cannot be disabled' : 'Optional - can be disabled'}
 </p>
 </div>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>
 </div>

 {/* Managing Cookies */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Settings className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Managing Your Cookie Preferences
 </h2>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
 <Card className="border-border">
 <CardHeader>
 <CardTitle className="text-lg sm:text-xl">Browser Settings</CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-sm sm:text-base text-muted-foreground mb-4">
 Most web browsers allow you to control cookies through their settings preferences. However, limiting cookies may affect your experience on our platform.
 </p>
 <div className="space-y-3">
 {cookieManagement.map((browser) => (
 <div key={browser.browser} className=" rounded-lg p-3 sm:p-4">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
 <span className="font-medium text-foreground text-sm sm:text-base">{browser.browser}</span>
 <a 
 href={browser.link} 
 target="_blank" 
 rel="noopener noreferrer"
 className="text-xs sm:text-sm text-primary hover:underline w-fit"
 >
 View Guide →
 </a>
 </div>
 <p className="text-xs text-muted-foreground">{browser.steps}</p>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 
 <Card className="border-border">
 <CardHeader>
 <CardTitle className="text-lg sm:text-xl">Our Cookie Consent Tool</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div className="bg-primary/5 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-3">
 <Settings className="w-5 h-5 text-primary" />
 <h4 className="font-bold text-foreground text-sm sm:text-base">Cookie Preferences</h4>
 </div>
 <p className="text-xs sm:text-sm text-muted-foreground mb-4">
 You can manage your cookie preferences through our built-in consent tool. Look for the cookie icon in the bottom-left corner of our website.
 </p>
 <div className="space-y-2">
 <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-2 h-2 rounded-full bg-green-500/100"></div>
 <span>Essential cookies are always enabled</span>
 </div>
 <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-2 h-2 rounded-full bg-yellow-500/100"></div>
 <span>Optional cookies can be toggled on/off</span>
 </div>
 </div>
 </div>
 
 <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800/30">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
 <div>
 <h4 className="font-bold text-foreground text-sm sm:text-base mb-1">Important Notice</h4>
 <p className="text-xs sm:text-sm text-muted-foreground">
 Disabling certain cookies may limit functionality. Essential cookies cannot be disabled as they are required for the platform to function properly.
 </p>
 </div>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Updates & Contact */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
 <Card className="border-border">
 <CardHeader>
 <CardTitle className="text-lg sm:text-xl">Policy Updates</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="flex items-center gap-3 mb-4">
 <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 <div>
 <h4 className="font-bold text-foreground text-sm sm:text-base">Last Updated</h4>
 <p className="text-xs sm:text-sm text-muted-foreground">January 4, 2025</p>
 </div>
 </div>
 <p className="text-xs sm:text-sm text-muted-foreground">
 We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our services. We will notify you of significant changes through email or platform notifications.
 </p>
 </CardContent>
 </Card>
 
 <Card className="border-border">
 <CardHeader>
 <CardTitle className="text-lg sm:text-xl">Contact Information</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div className="flex items-center gap-3">
 <Mail className="w-5 h-5 text-primary" />
 <div>
 <p className="text-xs text-muted-foreground">Email</p>
 <p className="text-sm sm:text-base text-foreground font-medium">privacy@shebeka.com</p>
 </div>
 </div>
 <div className="flex items-center gap-3">
 <Phone className="w-5 h-5 text-primary" />
 <div>
 <p className="text-xs text-muted-foreground">Phone</p>
 <p className="text-sm sm:text-base text-foreground font-medium">+251 11 123 4567</p>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Final Note */}
 <Card className="bg-gradient-to-r from-primary to-primary/80 border-0 text-white">
 <CardContent className="p-6 sm:p-8">
 <div className="flex flex-col md:flex-row items-center justify-between gap-4">
 <div>
 <h3 className="text-lg sm:text-xl font-bold mb-2">Your Privacy Matters</h3>
 <p className="text-sm sm:text-base text-white/90">We are committed to transparency about how we use cookies and similar technologies.</p>
 </div>
 <div className="text-xs sm:text-sm /20 backdrop-blur-sm rounded-xl px-3 sm:px-4 py-2 text-center">
 <p className="font-bold">Cookie Consent Required</p>
 <p className="text-white/80">In compliance with GDPR</p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </section>

 {/* Quick Links */}
 <section className="py-8 sm:py-12 ">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
 For more information, please also review:
 </p>
 <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
 <a 
 href="/privacy" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-card text-primary font-medium rounded-xl border border-primary/20 hover:bg-primary/5 transition-colors text-sm sm:text-base"
 >
 Privacy Policy
 </a>
 <a 
 href="/terms" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-card text-primary font-medium rounded-xl border border-primary/20 hover:bg-primary/5 transition-colors text-sm sm:text-base"
 >
 Terms of Service
 </a>
 <a 
 href="/contact" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-all text-sm sm:text-base"
 >
 Contact Privacy Team
 </a>
 </div>
 </div>
 </section>
 </div>
 );
}
